import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
  styled,
  tableCellClasses,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  InputAdornment,
  Popover,
  CircularProgress,
} from "@mui/material";
import Pagination from "../pagination";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { disabledFieldSx } from "../../constants/fieldStyles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload"; // Added for Deploy button
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import CustomTypography from "../../globalStyles/fonts";
import { ShopifyAppConfigService } from "../../utils/services/shopify-app-config-service";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import { toast } from "react-toastify";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
}));

const ShopifyAppConfigList = ({
  data,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  onDeleteSuccess,
}) => {
  const [configData, setConfigData] = useState([]);
  const startIndex = page * rowsPerPage;
  const [openConfirm, setOpenConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const [deployingItemId, setDeployingItemId] = useState(null);
  const openPopover = Boolean(anchorElPopover);
  const handleOpenConfirm = (item) => {
    setItemToDelete(item);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setItemToDelete(null);
  };

  const handlePopoverOpen = (event, item) => {
    setSelectedItem(item);
    event.stopPropagation();
    setAnchorElPopover(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorElPopover(null);
  };

  const confirmDelete = () => {
    deleteItem();
    // Implement delete logic here
    handleCloseConfirm();
  };
  useEffect(() => {
    setConfigData(
      data.map((item) => ({
        ...item,
      })),
    );
  }, [data]);
  const deleteItem = async () => {
    try {
      const response = await ShopifyAppConfigService.delete(itemToDelete?._id);
      onDeleteSuccess && onDeleteSuccess();
      return response;
    } catch (error) {
      console.error("Fetch failed:", error);
    }
  };
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // alert(`Copied: ${text}`) // You can replace this with a Snackbar or toast notification
  };

  const handleView = (item) => {
    setOpenViewModal(true);
    setSelectedItem(item);
    setAnchorElPopover(null);
  };

  const handleResendLink = async (item) => {
    try {
      const result = await ShopifyAppConfigService.resendLink(
        item.client_id,
        item.install_url,
      );
      console.log("Resend link successful:", result);
      setAnchorElPopover(null);
      toast.success("Installation link sent successfully!");
    } catch (error) {
      console.error("Failed to resend link:", error);
      toast.error("Failed to send installation link.");
    }
  };

  const handleDeploy = async (item) => {
    setDeployingItemId(item._id);
    try {
      toast.info("Deployment started. This may take a few minutes...");
      const result = await ShopifyAppConfigService.deploy(item._id);
      console.log("Deploy successful:", result);
      setAnchorElPopover(null);
      toast.success("App deployed successfully!");
    } catch (error) {
      console.error("Failed to deploy:", error);
      setAnchorElPopover(null);
      toast.error(error.response?.data?.message || "Failed to deploy app.");
    } finally {
      setDeployingItemId(null);
    }
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "name",
      title: "Name",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-primary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item.shop_name}
        </Typography>
      ),
    },
    {
      key: "business_name",
      title: "Business Name",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-primary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item.client_id?.business_name}
        </Typography>
      ),
    },
    {
      key: "email",
      title: "Email",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-primary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item.client_id?.email}
        </Typography>
      ),
    },
    {
      key: "action",
      title: "Action",
      render: (item, index) => (
        <Box sx={{ display: "flex", boxShadow: "none" }}>
          <IconButton
            aria-label="more actions"
            onClick={(e) => handlePopoverOpen(e, item)}
            sx={{
              color: openPopover ? "primary" : "inherit",
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorElPopover)}
            anchorEl={anchorElPopover}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: isMobile() ? "bottom" : "top",
              horizontal: isMobile() ? "center" : "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "none",
                border: "1px solid var(--border-color)",
                borderRadius: "4px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "10px",
                boxShadow: "none",
                gap: 1,
              }}
            >
              <Button
                style={{
                  color: "var(--text-tertiary)",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                onClick={() => handleView(selectedItem)}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <ViewIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-tertiary)"}
                    fontWeight={500}
                  >
                    View
                  </Typography>
                </Box>
              </Button>

              <Tooltip title="Resend Installation Link">
                <Button
                  style={{
                    color: "var(--text-tertiary)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  onClick={() => handleResendLink(selectedItem)}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <SendIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"var(--text-tertiary)"}
                      fontWeight={500}
                    >
                      Resend Link
                    </Typography>
                  </Box>
                </Button>
              </Tooltip>

              <Button
                style={{
                  color: "var(--text-tertiary)",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                onClick={() => handleDeploy(selectedItem)}
                disabled={deployingItemId === selectedItem?._id}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  {deployingItemId === selectedItem?._id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudUploadIcon />
                  )}
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-tertiary)"}
                    fontWeight={500}
                  >
                    Deploy
                  </Typography>
                </Box>
              </Button>

              <Button
                style={{
                  color: "var(--text-tertiary)",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                onClick={() => {
                  handleOpenConfirm(selectedItem);
                  setAnchorElPopover(null);
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <DeleteIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-tertiary)"}
                    fontWeight={500}
                  >
                    Delete
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Popover>
        </Box>
      ),
    },
  ];

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: "auto",
        paddingBottom: 20,
        marginTop: 5,
        height: "calc(100vh - 200px)",
      }}
    >
      <TableContainer sx={{ overflowY: "auto", paddingInline: 0.4 }}>
        <Table
          stickyHeader
          aria-label="Shopify App Configurations"
          sx={{ border: "1px solid var(--border-color)" }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((column) => (
                <StyledTableCell key={"shopify-config-" + column.key}>
                  <CustomTypography fontWeight={500} fontSize={14}>
                    {column.title}
                  </CustomTypography>
                </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {configData.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={5} align="center">
                  <Typography>No data available</Typography>
                </TableCell>
              </StyledTableRow>
            ) : (
              configData.map((item, index) => (
                <StyledTableRow key={item._id}>
                  {TableSchema.map((column) => (
                    <StyledTableCell key={column.key}>
                      {column.render ? (
                        column.render(item, index)
                      ) : (
                        <CustomTypography fontSize={14}>
                          {item[column.displayKey]}
                        </CustomTypography>
                      )}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <>
      <div>
        {renderListView()}
        <Pagination
          dataCount={dataCount}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          disableNext={dataCount <= rowsPerPage * (page + 1)}
        />
        <DeleteConfirmationModal
          title={`Are you sure to delete this shop ${itemToDelete?.shop_name}`}
          handleDelete={confirmDelete}
          handleClose={handleCloseConfirm}
          open={openConfirm}
        ></DeleteConfirmationModal>
        {/* <Dialog
          open={openConfirm}
          onClose={handleCloseConfirm}
          aria-labelledby="delete-confirmation-dialog"
          aria-describedby="delete-confirmation-description"
        >
          <DialogTitle id="delete-confirmation-dialog">Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-confirmation-description">
              Are you sure you want to delete this configuration? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog> */}
        <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)}>
          <DialogTitle>View Configuration</DialogTitle>
          <DialogContent style={{ width: "30vw" }}>
            {selectedItem && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Shop Name"
                  value={selectedItem.shop_name}
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={disabledFieldSx}
                />
                <TextField
                  label="App URL"
                  value={selectedItem.app_url}
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={disabledFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy App URL">
                          <IconButton
                            onClick={() => handleCopy(selectedItem.app_url)}
                          >
                            <FileCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Callback URL"
                  value={selectedItem.callback_url}
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={disabledFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy Callback URL">
                          <IconButton
                            onClick={() =>
                              handleCopy(selectedItem.callback_url)
                            }
                          >
                            <FileCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewModal(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default ShopifyAppConfigList;
